import { z } from 'zod';
import { isoDateTime, jsonObject, riskLevel, uuid } from './primitives';

/**
 * Approvals + notifications wire contracts (Master Spec §6.5, §10, §14). Shared
 * between the API (WP-8) and the web app (WP-10): the approval inbox, the approval
 * detail page (tool, exact arguments, reason, risk, affected, content snapshot),
 * the approve/reject decision request, and the web/SSE notification badge shape.
 *
 * These are the ONLY approval shapes that cross a boundary; no work package
 * invents its own variant (§8.2). Server-internal shapes (the `ApprovalCreator`
 * seam, the executor's re-validation) stay in `apps/server/src/approvals`.
 */

/** Lifecycle status of an approval (§6.5). */
export const approvalStatus = z.enum(['pending', 'approved', 'rejected', 'expired']);
export type ApprovalStatus = z.infer<typeof approvalStatus>;

/**
 * Pinned content for a by-reference action (§10). For `email.send(draftId)` the
 * gateway materializes recipients, subject, a body hash, and attachment hashes at
 * approval-record creation; at execution the connector re-fetches and recomputes,
 * refusing on mismatch ("draft changed after approval" → run BLOCKED). Kept as a
 * concrete, minimal shape — not raw provider payload (§9.6).
 */
export const approvalContentSnapshot = z.object({
  /** What kind of referenced object was pinned (e.g. 'email_draft'). */
  kind: z.string().min(1),
  /** The reference that will be materialized at execution (e.g. the draftId). */
  ref: z.string().min(1),
  recipients: z.array(z.string()).optional(),
  subject: z.string().optional(),
  /** SHA-256 of the canonical body text — the content, not a pointer (§10). */
  bodyHash: z.string().optional(),
  /** SHA-256 per attachment, order-stable. */
  attachmentHashes: z.array(z.string()).optional(),
});
export type ApprovalContentSnapshot = z.infer<typeof approvalContentSnapshot>;

/** A row in the pending-approvals inbox (§14). Minimal summary for the list. */
export const approvalSummary = z.object({
  id: uuid,
  runId: uuid,
  tool: z.string().min(1),
  reason: z.string(),
  risk: riskLevel,
  status: approvalStatus,
  expiresAt: isoDateTime,
  createdAt: isoDateTime,
});
export type ApprovalSummary = z.infer<typeof approvalSummary>;

/**
 * The approval detail page (§14): tool, EXACT arguments (JSON + human render on the
 * client), agent's reason, risk, affected recipients/records, and the pinned
 * content snapshot for by-reference sends. `argsHash` is surfaced so a client can
 * show the exact-arguments guarantee (§10). Decision fields are present once decided.
 */
export const approvalDetail = approvalSummary.extend({
  stepNo: z.number().int().positive(),
  arguments: jsonObject,
  argsHash: z.string(),
  contentSnapshot: approvalContentSnapshot.nullable().optional(),
  affected: jsonObject.nullable().optional(),
  decidedBy: uuid.nullable().optional(),
  decidedAt: isoDateTime.nullable().optional(),
});
export type ApprovalDetail = z.infer<typeof approvalDetail>;

/**
 * The approve/reject decision request body (§14). `note` is an optional rejection
 * note the run wakes with as an observation (§10). Single-use + idempotent
 * re-click safety are server-enforced, not part of the request.
 */
export const approvalDecisionRequest = z.object({
  decision: z.enum(['approved', 'rejected']),
  note: z.string().max(2000).optional(),
});
export type ApprovalDecisionRequest = z.infer<typeof approvalDecisionRequest>;

/** A web/SSE notification (§6.5, §14 badge). `payload` is a small routing object. */
export const notificationKind = z.enum([
  'approval_requested',
  'approval_decided',
  // #157: a schedule-triggered run finished (in-app delivery for scheduled runs).
  'scheduled_run_finished',
]);
export type NotificationKind = z.infer<typeof notificationKind>;

export const notificationView = z.object({
  id: uuid,
  kind: z.string().min(1),
  payload: jsonObject,
  channels: z.array(z.enum(['email', 'web'])),
  readAt: isoDateTime.nullable().optional(),
  createdAt: isoDateTime,
});
export type NotificationView = z.infer<typeof notificationView>;
